import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  Typography,
  Switch,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Button,
  InputLabel,
  Chip,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../redux/store';
import {
  fetchCommunityPolicy,
  updateCommunityPolicy,
  fetchChannelPolicy,
  updateChannelPolicy,
} from '../../redux/actions/notificationSettingsActions';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  card: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    fontSize: '1.25rem',
  },
  settingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  settingLabel: {
    fontSize: '1rem',
  },
  settingDescription: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  formControl: {
    minWidth: 150,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
}));

interface NotificationPolicyEditorProps {
  communityIdentifier: string;
  channelIdentifier?: string; // If provided, edits channel policy; otherwise community policy
}

const NotificationPolicyEditor: React.FC<NotificationPolicyEditorProps> = ({
  communityIdentifier,
  channelIdentifier,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isChannelPolicy = !!channelIdentifier;

  const {
    communityPolicy,
    communityPolicyLoading,
    communityPolicyError,
    channelPolicy,
    channelPolicyLoading,
    channelPolicyError,
    updateCommunityPolicyLoading,
    updateChannelPolicyLoading,
  } = useSelector((state: RootState) => state.notificationSettingsReducer);

  const policy = isChannelPolicy ? channelPolicy : communityPolicy;
  const policyLoading = isChannelPolicy ? channelPolicyLoading : communityPolicyLoading;
  const policyError = isChannelPolicy ? channelPolicyError : communityPolicyError;
  const updateLoading = isChannelPolicy ? updateChannelPolicyLoading : updateCommunityPolicyLoading;

  const [localPolicy, setLocalPolicy] = useState({
    policyMode: 'recommended' as 'recommended' | 'enforced',
    defaults: {
      globalFrequency: 'daily' as 'immediate' | 'daily' | 'off' | undefined,
      frequency: 'daily' as 'immediate' | 'daily' | 'off' | undefined,
      emailEnabled: true,
      pushEnabled: false,
    },
    digestSchedule: {
      sendTime: '08:00',
      timezone: 'America/Los_Angeles',
      intervalDays: 1,
    },
    emergencyEnabled: true,
    emergencyActors: ['admin', 'CommunityAdmin'] as string[],
  });

  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    // Always fetch community policy (needed for inheritance in channel policies)
    dispatch(fetchCommunityPolicy(communityIdentifier) as any);

    // Additionally fetch channel policy if editing a channel
    if (isChannelPolicy) {
      dispatch(fetchChannelPolicy(communityIdentifier, channelIdentifier) as any);
    }
  }, [dispatch, communityIdentifier, channelIdentifier, isChannelPolicy]);

  useEffect(() => {
    if (policy) {
      const communityPol = policy as any; // Type assertion to handle union type

      // For channel policies, use community policy digest schedule as default
      // Channel policy digestSchedule will override if it exists
      const inheritedDigestSchedule = isChannelPolicy && communityPolicy
        ? {
            sendTime: communityPol.digestSchedule?.sendTime || communityPolicy.digestSchedule?.sendTime || '08:00',
            timezone: communityPol.digestSchedule?.timezone || communityPolicy.digestSchedule?.timezone || 'America/Los_Angeles',
            intervalDays: communityPol.digestSchedule?.intervalDays || communityPolicy.digestSchedule?.intervalDays || 1,
          }
        : {
            sendTime: communityPol.digestSchedule?.sendTime || '08:00',
            timezone: communityPol.digestSchedule?.timezone || 'America/Los_Angeles',
            intervalDays: communityPol.digestSchedule?.intervalDays || 1,
          };

      setLocalPolicy({
        policyMode: policy.policyMode || 'recommended',
        defaults: {
          globalFrequency: isChannelPolicy ? undefined : (communityPol.defaults?.globalFrequency || 'daily'),
          frequency: isChannelPolicy ? (communityPol.defaults?.frequency || 'daily') : undefined,
          emailEnabled: policy.defaults?.emailEnabled ?? true,
          pushEnabled: policy.defaults?.pushEnabled ?? false,
        },
        digestSchedule: inheritedDigestSchedule,
        emergencyEnabled: isChannelPolicy ? true : (communityPol.emergencyEnabled ?? true),
        emergencyActors: isChannelPolicy ? ['admin', 'CommunityAdmin'] : (communityPol.emergencyActors || ['admin', 'CommunityAdmin']),
      });
    }
  }, [policy, isChannelPolicy, communityPolicy]);

  const handlePolicyModeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as 'recommended' | 'enforced';
    setLocalPolicy((prev) => ({ ...prev, policyMode: value }));
  };

  const handleDefaultFrequencyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as 'immediate' | 'daily' | 'off';
    setLocalPolicy((prev) => ({
      ...prev,
      defaults: {
        ...prev.defaults,
        ...(isChannelPolicy ? { frequency: value } : { globalFrequency: value }),
      },
    }));
  };

  const handleToggleChange = (field: 'emailEnabled' | 'pushEnabled') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.checked;
    setLocalPolicy((prev) => ({
      ...prev,
      defaults: {
        ...prev.defaults,
        [field]: value,
      },
    }));
  };

  const handleEmergencyEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalPolicy((prev) => ({
      ...prev,
      emergencyEnabled: event.target.checked,
    }));
  };

  const handleDigestScheduleChange = (field: 'sendTime' | 'timezone' | 'intervalDays') => (
    event: React.ChangeEvent<{ value: unknown }> | React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setLocalPolicy((prev) => ({
      ...prev,
      digestSchedule: {
        ...prev.digestSchedule,
        [field]: field === 'intervalDays' ? Number(value) : value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaveSuccess(false);

      console.log('[NotificationPolicyEditor] Saving policy...');
      console.log('[NotificationPolicyEditor] localPolicy state:', localPolicy);

      if (isChannelPolicy) {
        const payload = {
          policyMode: localPolicy.policyMode,
          defaults: {
            frequency: localPolicy.defaults.frequency,
            emailEnabled: localPolicy.defaults.emailEnabled,
            pushEnabled: localPolicy.defaults.pushEnabled,
          },
          digestSchedule: localPolicy.digestSchedule, // Channel can override community digest schedule
        };
        console.log('[NotificationPolicyEditor] Dispatching updateChannelPolicy with:', payload);
        const result = await dispatch(
          updateChannelPolicy(communityIdentifier, channelIdentifier, payload) as any
        );
        console.log('[NotificationPolicyEditor] updateChannelPolicy result:', result);
      } else {
        const payload = {
          policyMode: localPolicy.policyMode,
          defaults: {
            globalFrequency: localPolicy.defaults.globalFrequency,
            emailEnabled: localPolicy.defaults.emailEnabled,
            pushEnabled: localPolicy.defaults.pushEnabled,
          },
          digestSchedule: localPolicy.digestSchedule,
          emergencyEnabled: localPolicy.emergencyEnabled,
          emergencyActors: localPolicy.emergencyActors,
        };
        console.log('[NotificationPolicyEditor] Dispatching updateCommunityPolicy with:', payload);
        const result = await dispatch(
          updateCommunityPolicy(communityIdentifier, payload) as any
        );
        console.log('[NotificationPolicyEditor] updateCommunityPolicy result:', result);
      }

      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      // Error is handled by Redux
      console.error('[NotificationPolicyEditor] Failed to save policy:', error);
    }
  };

  if (policyLoading && !policy) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography variant="h4" gutterBottom>
        {isChannelPolicy ? 'Channel' : 'Community'} Notification Policy
      </Typography>

      {policyError && (
        <Box mb={2} p={2} style={{ backgroundColor: '#ffebee', borderRadius: 8 }}>
          <Typography color="error" style={{ fontWeight: 600 }}>Error Loading Policy</Typography>
          <Typography variant="body2" color="error">{policyError}</Typography>
          <Typography variant="caption" style={{ display: 'block', marginTop: 8 }}>
            The policy will be created automatically when you save for the first time.
          </Typography>
        </Box>
      )}

      {/* Info Banner */}
      <Box mb={2} p={2} style={{ backgroundColor: '#e3f2fd', borderRadius: 8, border: '1px solid #2196f3' }}>
        <Typography variant="body2" style={{ fontWeight: 600, color: '#1976d2', marginBottom: 4 }}>
          {isChannelPolicy ? '📢 Channel Notification Policy' : '🌐 Community Notification Policy'}
        </Typography>
        <Typography variant="body2" style={{ color: '#1976d2' }}>
          {isChannelPolicy
            ? `Configure default notification settings for this specific channel. These settings apply only to this channel.`
            : `Configure default notification settings for all channels in this community. Individual channel policies can override these settings.`
          }
        </Typography>
        <Typography variant="caption" style={{ display: 'block', marginTop: 8, color: '#1976d2' }}>
          💡 <strong>Tip:</strong> Changes take effect immediately when you click "Save Policy". Users will see warnings if policies are enforced.
        </Typography>
      </Box>

      {/* Policy Mode */}
      <Card className={classes.card}>
        <Typography className={classes.sectionTitle}>Policy Mode</Typography>
        <Divider />

        <Box className={classes.settingRow} mt={2}>
          <Box>
            <Typography className={classes.settingLabel}>Policy Enforcement</Typography>
            <Typography className={classes.settingDescription}>
              {localPolicy.policyMode === 'enforced'
                ? 'Users cannot override these settings'
                : 'Users can override these default settings'}
            </Typography>
          </Box>
          <FormControl className={classes.formControl}>
            <Select value={localPolicy.policyMode} onChange={handlePolicyModeChange}>
              <MenuItem value="recommended">Recommended</MenuItem>
              <MenuItem value="enforced">Enforced</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Default Settings */}
      <Card className={classes.card}>
        <Typography className={classes.sectionTitle}>Default Settings</Typography>
        <Divider />

        <Box className={classes.settingRow} mt={2}>
          <Box>
            <Typography className={classes.settingLabel}>Email Notifications</Typography>
            <Typography className={classes.settingDescription}>
              Enable email notifications by default
            </Typography>
          </Box>
          <Switch
            checked={localPolicy.defaults.emailEnabled}
            onChange={handleToggleChange('emailEnabled')}
            color="primary"
          />
        </Box>

        <Box className={classes.settingRow}>
          <Box>
            <Typography className={classes.settingLabel}>Push Notifications</Typography>
            <Typography className={classes.settingDescription}>
              Enable push notifications by default (opt-in)
            </Typography>
          </Box>
          <Switch
            checked={localPolicy.defaults.pushEnabled}
            onChange={handleToggleChange('pushEnabled')}
            color="primary"
          />
        </Box>

        <Box className={classes.settingRow}>
          <Box>
            <Typography className={classes.settingLabel}>Default Frequency</Typography>
            <Typography className={classes.settingDescription}>
              How often users receive notifications by default
            </Typography>
          </Box>
          <FormControl className={classes.formControl}>
            <Select
              value={isChannelPolicy ? localPolicy.defaults.frequency : localPolicy.defaults.globalFrequency}
              onChange={handleDefaultFrequencyChange}
            >
              <MenuItem value="immediate">Immediate</MenuItem>
              <MenuItem value="daily">Daily Digest</MenuItem>
              <MenuItem value="off">Off</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Digest Schedule */}
      <Card className={classes.card}>
        <Typography className={classes.sectionTitle}>
          {isChannelPolicy ? '📅 Channel Digest Schedule' : '📅 Digest Schedule'}
        </Typography>
        <Divider />

        <Box mt={2} mb={2} p={2} style={{ backgroundColor: '#fff3e0', borderRadius: 8, border: '1px solid #ff9800' }}>
          <Typography variant="body2" style={{ fontWeight: 600, color: '#e65100', marginBottom: 4 }}>
            📬 Daily Digest Configuration
          </Typography>
          <Typography variant="body2" style={{ color: '#e65100' }}>
            {isChannelPolicy
              ? `Configure when and how often digest emails are sent for this channel. Leave blank to inherit from community settings.`
              : `Configure when and how often digest emails are sent across all channels in this community. Channels can override these settings.`
            }
          </Typography>
        </Box>

        <Box className={classes.settingRow}>
          <Box>
            <Typography className={classes.settingLabel}>Send Time (24-hour format)</Typography>
            <Typography className={classes.settingDescription}>
              What time should digests be sent? (e.g., 08:00 for 8 AM)
            </Typography>
          </Box>
          <TextField
            type="time"
            value={localPolicy.digestSchedule.sendTime}
            onChange={handleDigestScheduleChange('sendTime')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }} // 5 min intervals
            style={{ width: 150 }}
          />
        </Box>

        <Box className={classes.settingRow}>
          <Box>
            <Typography className={classes.settingLabel}>Timezone</Typography>
            <Typography className={classes.settingDescription}>
              Timezone for sending digests (IANA format)
            </Typography>
          </Box>
          <FormControl className={classes.formControl} style={{ minWidth: 250 }}>
            <Select
              value={localPolicy.digestSchedule.timezone}
              onChange={handleDigestScheduleChange('timezone')}
            >
              <MenuItem value="America/Los_Angeles">America/Los_Angeles (PT)</MenuItem>
              <MenuItem value="America/Denver">America/Denver (MT)</MenuItem>
              <MenuItem value="America/Chicago">America/Chicago (CT)</MenuItem>
              <MenuItem value="America/New_York">America/New_York (ET)</MenuItem>
              <MenuItem value="America/Toronto">America/Toronto (ET)</MenuItem>
              <MenuItem value="America/Vancouver">America/Vancouver (PT)</MenuItem>
              <MenuItem value="UTC">UTC</MenuItem>
              <MenuItem value="Europe/London">Europe/London</MenuItem>
              <MenuItem value="Europe/Paris">Europe/Paris</MenuItem>
              <MenuItem value="Asia/Tokyo">Asia/Tokyo</MenuItem>
              <MenuItem value="Australia/Sydney">Australia/Sydney</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box className={classes.settingRow}>
          <Box>
            <Typography className={classes.settingLabel}>Digest Interval (Days)</Typography>
            <Typography className={classes.settingDescription}>
              How many days of content to include in each digest (1-7 days)
            </Typography>
          </Box>
          <FormControl className={classes.formControl}>
            <Select
              value={localPolicy.digestSchedule.intervalDays}
              onChange={handleDigestScheduleChange('intervalDays')}
            >
              <MenuItem value={1}>1 Day (Daily)</MenuItem>
              <MenuItem value={2}>2 Days</MenuItem>
              <MenuItem value={3}>3 Days</MenuItem>
              <MenuItem value={7}>7 Days (Weekly)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box mt={2} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 8 }}>
          <Typography variant="caption" color="textSecondary">
            💡 <strong>Example:</strong> With settings "08:00, America/Los_Angeles, 1 Day", digests will be sent
            every day at 8:00 AM Pacific Time, containing all notifications from the previous 24 hours.
          </Typography>
        </Box>
      </Card>

      {/* Emergency Broadcasts (Community Only) */}
      {!isChannelPolicy && (
        <Card className={classes.card}>
          <Typography className={classes.sectionTitle}>Emergency Broadcasts</Typography>
          <Divider />

          <Box className={classes.settingRow} mt={2}>
            <Box>
              <Typography className={classes.settingLabel}>Emergency Notifications</Typography>
              <Typography className={classes.settingDescription}>
                Allow emergency broadcasts that override user preferences
              </Typography>
            </Box>
            <Switch
              checked={localPolicy.emergencyEnabled}
              onChange={handleEmergencyEnabledChange}
              color="primary"
            />
          </Box>

          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Authorized Roles (Admin and CommunityAdmin only)
            </Typography>
            <Box display="flex" flexWrap="wrap" mt={1} style={{ gap: '8px' }}>
              {localPolicy.emergencyActors.map((actor) => (
                <Chip key={actor} label={actor} color="primary" />
              ))}
            </Box>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
              Per requirements: Moderators cannot send emergency broadcasts
            </Typography>
          </Box>
        </Card>
      )}

      {/* Info Box */}
      <Card className={classes.card} style={{ backgroundColor: '#f5f5f5' }}>
        <Typography variant="body2" color="textSecondary">
          <strong>Policy Precedence:</strong> {isChannelPolicy ? 'Channel' : 'Community'} policies serve as defaults.
          User per-post subscriptions always have the highest priority, followed by per-channel subscriptions,
          then enforced policies, then recommended policies.
        </Typography>
      </Card>

      {/* Success Message */}
      {saveSuccess && (
        <Box mb={2} p={2} style={{ backgroundColor: '#e8f5e9', borderRadius: 8, border: '1px solid #4caf50' }}>
          <Typography style={{ fontWeight: 600, color: '#2e7d32' }}>
            ✓ Policy Saved Successfully!
          </Typography>
          <Typography variant="body2" style={{ color: '#2e7d32' }}>
            The notification policy has been updated and is now active. Users will see this policy applied to their notification settings.
          </Typography>
        </Box>
      )}

      <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
            {localPolicy.policyMode === 'enforced'
              ? '⚠️ Enforced mode: Users cannot change these settings'
              : '💡 Recommended mode: Users can override these defaults'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={updateLoading}
          style={{ minWidth: 120 }}
        >
          {updateLoading ? <CircularProgress size={24} /> : 'Save Policy'}
        </Button>
      </Box>
    </Box>
  );
};

export default NotificationPolicyEditor;
